import { useNavigate } from 'react-router-dom';
import NavBar from '../../components/feature/NavBar';
import TabBar from '../../components/feature/TabBar';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

export default function Home() {
  const navigate = useNavigate();
  
  const features = [
    {
      icon: 'ri-book-open-line',
      title: 'Decodable Words',
      description: 'Practice phonics with systematic word patterns'
    },
    {
      icon: 'ri-gamepad-line',
      title: 'Interactive Games',
      description: 'Learn through fun multiplayer experiences'
    },
    {
      icon: 'ri-group-line',
      title: 'Classroom Ready',
      description: 'Teachers and students play together'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Track Progress',
      description: 'Monitor reading skill development'
    }
  ];
  
  const recentActivities = [
    { game: 'Phonics Snakes & Ladders', score: '85%', time: '2 hours ago' },
    { game: 'Word Puzzle', score: '92%', time: '1 day ago' },
    { game: 'Spelling Race', score: '78%', time: '3 days ago' }
  ];
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
      <NavBar />
      <TabBar />
      
      <div className="pt-20 md:pt-24 pb-20 md:pb-8">
        <div className="max-w-7xl mx-auto px-6">
          {/* Hero Section */}
          <div className="text-center mb-12 md:mb-16">
            <div className="flex justify-center mb-6">
              <img 
                src="https://static.readdy.ai/image/1624bf5855e87197e9f976fe97d0b1c6/4a99c4ae32eb70e334047e2a84452586.png" 
                alt="SkillSprout mascot" 
                className="w-24 h-24 md:w-32 md:h-32"
              />
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-4">
              Welcome to <span className="text-[#2d5a35]">SkillSprout</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Interactive literacy games that make learning phonics and reading fun for students and teachers
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/games')}
                className="px-8 py-3 text-lg"
              >
                <i className="ri-gamepad-line mr-2"></i>
                Start Playing
              </Button>
              <Button 
                onClick={() => navigate('/progress')}
                variant="outline"
                className="px-8 py-3 text-lg"
              >
                <i className="ri-bar-chart-line mr-2"></i>
                View Progress
              </Button>
            </div>
          </div>
          
          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 md:mb-16">
            {features.map((feature, index) => (
              <Card key={index} className="text-center h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-[#2d5a35] to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${feature.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="font-bold text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </Card>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Quick Start */}
            <Card className="h-full">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2d5a35] to-green-600 rounded-xl flex items-center justify-center">
                  <i className="ri-play-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">Quick Start</h3>
                  <p className="text-gray-600 text-sm">Jump into your favorite reading games</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/game/snakes-ladders')}
                  className="w-full flex items-center justify-between p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      <i className="ri-gamepad-line text-[#2d5a35]"></i>
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-gray-800">Phonics Snakes & Ladders</div>
                      <div className="text-sm text-gray-600">Practice decodable words</div>
                    </div>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400"></i>
                </button>
                
                <button 
                  onClick={() => navigate('/games')}
                  className="w-full flex items-center justify-between p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      <i className="ri-grid-line text-[#2d5a35]"></i>
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-gray-800">All Games</div>
                      <div className="text-sm text-gray-600">Browse all literacy games</div>
                    </div>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400"></i>
                </button>
              </div>
            </Card>
            
            {/* Recent Activity */}
            <Card className="h-full">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <i className="ri-time-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">Recent Activity</h3>
                  <p className="text-gray-600 text-sm">Your latest game sessions</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {recentActivities.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-800 text-sm">{activity.game}</div>
                      <div className="text-xs text-gray-500">{activity.time}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-[#2d5a35]">{activity.score}</div>
                    </div>
                  </div>
                ))}
                
                <button 
                  onClick={() => navigate('/progress')}
                  className="w-full mt-4 py-2 text-sm text-[#2d5a35] font-medium hover:bg-green-50 rounded-lg transition-colors"
                >
                  View All Activity →
                </button>
              </div>
            </Card>
          </div>
          
          {/* Call to Action */}
          <div className="text-center mt-12 md:mt-16">
            <Card className="max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2d5a35] to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-graduation-cap-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">Ready to Start Learning?</h3>
              <p className="text-gray-600 mb-6">
                Join thousands of students and teachers who are improving their reading skills with SkillSprout
              </p>
              <Button 
                onClick={() => navigate('/games')}
                className="px-8 py-3 text-lg"
              >
                Explore Games
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
